
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

import {
    Autocomplete,
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import {
    getProductionLogs,
    saveProductionLogApproval,
} from "../../services/assemblyService";

import { getAuthToken } from '../../utils/token';
import config from '../../config/config';

const ApprovedAssembly = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);
    const [savingApproval, setSavingApproval] = useState(false);

    const [error, setError] = useState("");
    const [total, setTotal] = useState(0);

    const [productOptions, setProductOptions] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [approvalOpen, setApprovalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    // Display notification
    const showNotification = (message, severity = "success") => {
        setNotification({
            open: true,
            message,
            severity,
        });
    };

    // Fetch assembly products for the dropdown
    const fetchProductList = useCallback(async () => {
        const token = getAuthToken();

        if (!token) {
            showNotification(
                "Authentication token not found.",
                "error"
            );
            return;
        }

        setProductsLoading(true);

        try {
            const response = await axios.get(
                `${config.API}/product/list`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            setProductOptions(
                response?.data?.products?.map((plist) => ({
                    value: plist.id,
                    label: plist.name,
                })) || []
            );
        } catch (err) {
            console.error("Error fetching products:", err);

            showNotification(
                err.response?.data?.message ||
                "Failed to load assembly products.",
                "error"
            );
        } finally {
            setProductsLoading(false);
        }
    }, []);

    // Fetch production logs
    const fetchProductionLogs = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await getProductionLogs({
                page: paginationModel.page + 1,
                per_page: paginationModel.pageSize,
            });

            if (response?.success) {
                setRows(
                    (response.data || []).map((item) => ({
                        ...item,
                        id: item.id,
                    }))
                );

                setTotal(Number(response.total ?? 0));
            } else {
                setRows([]);
                setTotal(0);

                setError(
                    response?.message ||
                    "Failed to retrieve production logs."
                );
            }
        } catch (err) {
            console.error("Error fetching production logs:", err);

            setRows([]);
            setTotal(0);

            setError(
                err.response?.data?.message ||
                err.message ||
                "Unable to load production logs."
            );
        } finally {
            setLoading(false);
        }
    }, [paginationModel]);

    useEffect(() => {
        fetchProductList();
    }, [fetchProductList]);

    useEffect(() => {
        fetchProductionLogs();
    }, [fetchProductionLogs]);

    // Open approval dialog
    const openApprovalDialog = (row) => {
        setSelectedLog(row);
        setSelectedProduct(null);
        setApprovalOpen(true);
    };

    // Close approval dialog
    const closeApprovalDialog = () => {
        if (savingApproval) return;

        setApprovalOpen(false);
        setSelectedLog(null);
        setSelectedProduct(null);
    };

    // Save approval
    const handleSaveApproval = async () => {
        if (!selectedLog) {
            showNotification(
                "Please select a production log.",
                "warning"
            );
            return;
        }

        if (!selectedProduct) {
            showNotification(
                "Please select an assembly product.",
                "warning"
            );
            return;
        }

        setSavingApproval(true);

        try {
            const response = await saveProductionLogApproval({
                production_log_id: selectedLog.id,
                assemle_item_id: selectedProduct.value,
            });

            if (!response?.success) {
                throw new Error(
                    response?.message || "Approval failed."
                );
            }

            setApprovalOpen(false);
            setSelectedLog(null);
            setSelectedProduct(null);

            showNotification(
                response.message ||
                "Assembly record created successfully.",
                "success"
            );

            // Refresh the grid after successful approval
            await fetchProductionLogs();
        } catch (err) {
            console.error("Error saving approval:", err);

            showNotification(
                err.response?.data?.message ||
                err.message ||
                "Failed to save approval.",
                "error"
            );
        } finally {
            setSavingApproval(false);
        }
    };

    // DataGrid columns
    const columns = [
        {
            field: "id",
            headerName: "ID",
            width: 50,
        },
        {
            field: "date",
            headerName: "Production Date",
            width: 120,
            valueFormatter: (value) =>
                value
                    ? new Date(`${value}T00:00:00`).toLocaleDateString(
                        "en-IN",
                        {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                        }
                    )
                    : "",
        },
        {
            field: "item",
            headerName: "Item",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "shift",
            headerName: "Shift",
            width: 90,
        },
        {
            field: "unit_produced",
            headerName: "Units Consumed",
            type: "number",
            width: 120,
            align: "right",
            headerAlign: "right",
        },
        {
            field: "notes",
            headerName: "Notes",
            minWidth: 120,
            flex: 0.6,
            valueGetter: (value) => value ?? "-",
        },
        {
            field: "actions",
            headerName: "Approval",
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => openApprovalDialog(params.row)}
                >
                    Approve
                </Button>
            ),
        },
    ];

    return (
        <Box>
            {/* Page header */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                sx={{ mb: 1 }}
            >
                <Typography variant="h6" fontWeight={600}>
                 Assembly Approval
                </Typography>
            </Stack>
            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                </Alert>
            )}

            {/* Production logs grid */}
            <Box sx={{ height: 200, width: "100%" }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={total}
                    pagination
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    sx={{
                        borderRadius: 2,
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#f5f5f5",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 700,
                        },
                        "& .MuiDataGrid-cell": {
                            display: "flex",
                            alignItems: "center",
                        },
                    }}
                />
            </Box>

            {/* Approval dialog */}
            <Dialog
                open={approvalOpen}
                onClose={closeApprovalDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Approve Operator Production Log
                </DialogTitle>

                <Divider />

                <DialogContent>
                    {selectedLog && (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            <TextField
                                label="Log ID"
                                value={selectedLog.id}
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />

                            <TextField
                                label="Assembly Date"
                                value={selectedLog.date || ""}
                                fullWidth
                                InputProps={{ readOnly: false }}
                            />

                            <TextField
                                label="Item"
                                value={selectedLog.item || ""}
                                fullWidth
                                multiline
                                InputProps={{ readOnly: true }}
                            />

                            <TextField
                                label="Shift"
                                value={selectedLog.shift || ""}
                                fullWidth
                                InputProps={{ readOnly: false }}
                            />

                            <TextField
                                label="Units Conssumed"
                                value={selectedLog.unit_produced ?? 0}
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />

                            <TextField 
                                label="Notes"
                                value={selectedLog.notes || "-"}
                                fullWidth
                               // multiline
                                InputProps={{ readOnly: false }}
                            />



                            {/* Assembly product dropdown */}
                            <Autocomplete
                                options={productOptions}
                                value={selectedProduct}
                                onChange={(event, newValue) => {
                                    setSelectedProduct(newValue);
                                }}
                                getOptionLabel={(option) => option?.label || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option?.value === value?.value
                                }
                                loading={productsLoading}
                                disabled={savingApproval}
                                fullWidth
                                noOptionsText="No products found"
                                loadingText="Loading products..."
                                renderInput={(params) => {
                                    const {
                                        InputProps = {},
                                        ...textFieldParams
                                    } = params;

                                    return (
                                        <TextField
                                            {...textFieldParams}
                                            label="Assembly Product"
                                            placeholder="Search and select product"
                                            required
                                            helperText={
                                                selectedProduct
                                                    ? `Product ID: ${selectedProduct.value}`
                                                    : "Select the assembly product for approval"
                                            }
                                            InputProps={{
                                                ...InputProps,
                                                endAdornment: (
                                                    <>
                                                        {productsLoading && (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={20}
                                                            />
                                                        )}
                                                        {InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    );
                                }}
                            />
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 2, pb: 1 }}>
                    <Button
                        onClick={closeApprovalDialog}
                        disabled={savingApproval}
                        color="inherit"
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleSaveApproval}
                        disabled={
                            savingApproval ||
                            productsLoading ||
                            !selectedProduct
                        }
                    >
                        {savingApproval
                            ? "Saving..."
                            : "Confirm Approval"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success and error notifications */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() =>
                    setNotification((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
            >
                <Alert
                    severity={notification.severity}
                    variant="filled"
                    onClose={() =>
                        setNotification((prev) => ({
                            ...prev,
                            open: false,
                        }))
                    }
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ApprovedAssembly;
